// Stitched-clip segment math.
//
// The single playback primitive on this surface is a list of `ClipSegment`s
// that the engine plays back-to-back ("stitch"). Two highly-tested
// operations live here:
//
//   - `segAtVirtual(segments, ms)` — given a virtual timeline offset (the
//     position into the stitched clip), return the (segment-index, offset-
//     within-segment) tuple to seek to. Used by the scrubber, `seekToVirtual`,
//     and the grid clock-fan-out.
//
//   - `synthesizeSegments(start, dur)` — when /api/clips:resolve fails (or
//     the local mock is off), build an opinionated stitch plan of
//     <=10-min cells so the rest of the UI keeps rendering. Mirrors the
//     mock's fallback (LLD 13 §17).
//
// Both are pure; `useStitchPlayer` and `useGridEngine` keep all I/O.

import type { ClipSegment, ClipTier } from "@/api/clipsClient";

/** AVA caps any one render at 10 min (HD 504s past ~10 min; LLD 13 §10). */
export const SEG_CAP_MS = 10 * 60_000;

/**
 * Display state of a segment in the stitch plan. Owned by the engine and
 * mutated as segments load / play / advance — the timeline and "Stitched"
 * render-bar both read it. Kept off the wire shape so refetching doesn't
 * stomp on the UI's transient view-state.
 */
export type SegmentState = "queued" | "loading" | "playing" | "ready" | "error";

export interface PlayableSegment extends ClipSegment {
  state: SegmentState;
}

/**
 * Map a virtual-clip time (ms from clip start, in the stitched timeline) to
 * a (segment index, offset within that segment in ms) tuple.
 *
 * Past the end of the clip clamps to the last segment's last instant; this
 * is what the scrubber wants when the user yanks past the right edge.
 */
export function segAtVirtual(
  segments: readonly { duration_ms: number }[],
  ms: number,
): { i: number; offset: number } {
  if (segments.length === 0) return { i: 0, offset: 0 };
  if (ms < 0) return { i: 0, offset: 0 };
  let acc = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    const d = seg.duration_ms;
    // Past the last segment? clamp to its last instant.
    if (i === segments.length - 1) return { i, offset: Math.min(d, Math.max(0, ms - acc)) };
    if (ms < acc + d) return { i, offset: Math.max(0, ms - acc) };
    acc += d;
  }
  // Unreachable: covered by the i===len-1 guard above.
  return { i: 0, offset: 0 };
}

/**
 * Sum of `duration_ms` across all segments. The total length the scrubber
 * maps onto.
 */
export function totalDurationMs(
  segments: readonly { duration_ms: number }[],
): number {
  let t = 0;
  for (const s of segments) t += s.duration_ms;
  return t;
}

/**
 * Inverse of segAtVirtual — given a segment index + offset, return the
 * virtual-clip time that resolves to. Used when an `<video>.timeupdate`
 * fires and we need to update the playhead.
 */
export function virtualAtSeg(
  segments: readonly { duration_ms: number }[],
  segIndex: number,
  offsetMs: number,
): number {
  let acc = 0;
  const upTo = Math.min(segIndex, segments.length);
  for (let i = 0; i < upTo; i++) acc += segments[i]!.duration_ms;
  return acc + Math.max(0, offsetMs);
}

/**
 * Synthesize a stitch plan when the resolve endpoint is unavailable.
 * The synthetic segments have `playback_url=null` so the engine renders
 * them as placeholders rather than firing doomed `<video>.src=...` GETs.
 *
 * Mirrors the mock prototype's offline fallback so the surface stays
 * usable when the BFF hasn't shipped yet.
 */
export function synthesizeSegments(
  startMs: number,
  durMs: number,
  tier: ClipTier,
): PlayableSegment[] {
  const n = Math.max(1, Math.ceil(durMs / SEG_CAP_MS));
  const out: PlayableSegment[] = [];
  for (let i = 0; i < n; i++) {
    const segStart = startMs + i * SEG_CAP_MS;
    const segDur = Math.min(SEG_CAP_MS, durMs - i * SEG_CAP_MS);
    out.push({
      index: i,
      start_ms: segStart,
      duration_ms: segDur,
      playback_url: null,
      tier,
      bytes: null,
      partial: false,
      state: "queued",
    });
  }
  return out;
}

/**
 * Decorate raw wire segments with engine state. Initial state is `queued`
 * for everything; the engine flips them to `loading`/`playing`/`ready`
 * as it advances.
 */
export function withInitialState(segs: readonly ClipSegment[]): PlayableSegment[] {
  return segs.map((s) => ({ ...s, state: "queued" as SegmentState }));
}
